import {
  BarList,
  ReportSection,
  ReportTable,
  Stat,
  StatGrid,
} from "../ReportPrimitives";
import type { TasksReport } from "../reportTables";

const pretty = (value: string) =>
  value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

/**
 * Tasks & workload. Open/overdue are current-state counts while "completed" is
 * windowed, mixing the two is deliberate: "what is on my plate now" and "what
 * did we get through" are both the question being asked here.
 */
export const TasksPanel = ({ data }: { data: TasksReport }) => (
  <div className="flex flex-col gap-4">
    <StatGrid>
      <Stat label="Open" value={String(data.open)} helper="Not yet done" />
      <Stat
        label="Overdue"
        value={String(data.overdue)}
        helper="Past their due date"
        tone={data.overdue > 0 ? "negative" : "default"}
      />
      <Stat
        label="Due in 7 days"
        value={String(data.due_next_7_days)}
        helper="Coming up"
      />
      <Stat
        label="Completed"
        value={String(data.completed)}
        helper="In this period"
        tone="positive"
      />
    </StatGrid>

    <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <ReportSection
        title="Open tasks by priority"
        subtitle="Overdue shown beneath"
      >
        <BarList
          items={data.by_priority.map((row) => ({
            label: pretty(row.priority),
            value: row.count,
            display: String(row.count),
            sub: row.overdue > 0 ? `${row.overdue} overdue` : undefined,
          }))}
          empty="No open tasks."
        />
      </ReportSection>

      <ReportSection title="Workload by owner" subtitle="Who is carrying what">
        <ReportTable
          columns={["Owner", "Open", "Overdue", "Done"]}
          align={["left", "right", "right", "right"]}
          rows={data.by_owner.map((row) => [
            row.name,
            row.open,
            row.overdue,
            row.completed,
          ])}
          empty="No tasks assigned."
        />
      </ReportSection>
    </div>

    <ReportSection
      title="Jobs by status"
      subtitle="Whole workspace, current state"
    >
      <BarList
        items={data.jobs_by_status.map((row) => ({
          label: pretty(row.status),
          value: row.count,
          display: String(row.count),
        }))}
        empty="No jobs yet."
      />
    </ReportSection>
  </div>
);
